import React, { useRef, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Form, Button, Row, Col, Table, Tabs, Tab, Modal } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useWatch } from 'react-hook-form';
import SignatureCanvas from 'react-signature-canvas';
import { SUBMIT_FORM, VIEW_FORM } from 'actions/formActions';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { cookie } from 'utils/cookies';

/**
 * HTCableForm — two-level approval flow aligned with APFC
 *
 * Per-level pipeline: draft → CONTRACTOR → MANAGER → QUALITY → approved
 * Common status rules (saved as form_data.status):
 *   - CONTRACTOR if both L1 & L2 are CONTRACTOR
 *   - MANAGER if both are MANAGER
 *   - QUALITY if both are QUALITY
 *   - approved if both are approved
 *   - otherwise draft
 *
 * Edit rules:
 *   - L1 equipment/visual/physical: only Engineer when L1 === 'draft'
 *   - L2 fields: only Engineer when L2 === 'draft'
 *   - Per-level other comments (l1_other_comments, l2_other_comments): editable by ALL until that level is approved
 *   - Signatures per level:
 *       • charge_engineer → Engineer @ 'draft'
 *       • contractor → Contractor @ 'CONTRACTOR'
 *       • quality → Quality @ 'QUALITY'
 *       • optional → read-only
 *   - Contractor can open a Level tab only when that level status === 'CONTRACTOR'
 */

const HTCableForm = ({ role = cookie.get('user-role') }) => {
  const dispatch = useDispatch();
  const { viewFormData } = useSelector((state) => state.checklist);
  const { formId, stationId } = useParams();
  const sigCanvas = useRef(null);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [currentSignature, setCurrentSignature] = useState({ level: '', role: '' });
  const [isLoading, setIsLoading] = useState(true);
  const dataLoadedRef = useRef(false);

  const { register, control, handleSubmit, setValue, getValues } = useForm({
    defaultValues: {
      station_id: stationId || '',
      project_name: '',
      equipment: '',
      manufacturer: '',
      output: '',
      visual_checks: [
        { description: 'Have the correct methods of cable termination and tagging been checked?', status: '', remark: '' },
        { description: 'Has the cable TC been checked?', status: '', remark: '' },
      ],
      physical_checks: [
        { description: 'Are the busbar joints properly connected and tightened?', status: '', remark: '' },
        { description: 'Has the proper size lugs been used with the correct lugging tool?', status: '', remark: '' },
      ],
      l1_comments: {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      },
      l1_other_comments: '',
      l1_status: 'draft',

      all_cables_healthy: '',
      testing_commissioning: '',
      cables: Array(8)
        .fill({
          cable_number: 0,
          status: '',
          tag_no: '',
          cable_size: '',
          from: '',
          to: '',
          cable_length: '',
          r_y_continuity: '',
          y_b_continuity: '',
          b_r_continuity: '',
          r_n_continuity: '',
          y_n_continuity: '',
          b_n_continuity: '',
          r_e_continuity: '',
          y_e_continuity: '',
          b_e_continuity: '',
          n_e_continuity: '',
          r_y_ir_value: '',
          y_b_ir_value: '',
          b_r_ir_value: '',
          r_n_ir_value: '',
          y_n_ir_value: '',
          b_n_ir_value: '',
          r_e_ir_value: '',
          y_e_ir_value: '',
          b_e_ir_value: '',
          n_e_ir_value: '',
          healthy: '',
          extra_length_side: '',
        })
        .map((item, index) => ({ ...item, cable_number: index + 1 })),
      instruments: [
        { description: 'IR Tester', serial: '', range: '', calibration: '' },
        { description: 'Digital Multi meter / Tong Tester', serial: '', range: '', calibration: '' },
      ],
      l2_comments: {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      },
      l2_other_comments: '',
      l2_observations: '',
      l2_status: 'draft',
      status: 'draft', // overall/common status
    },
  });

  const formValues = useWatch({ control });
  const l1_status = useWatch({ control, name: 'l1_status' });
  const l2_status = useWatch({ control, name: 'l2_status' });
  const l1_manager_id = useWatch({ control, name: 'l1_manager_id' });
  const l2_manager_id = useWatch({ control, name: 'l2_manager_id' });

  // Engineer-only editability for core content
  const isLevel1Editable = !l1_manager_id && ((role === 'engineer' && (l1_status === 'draft' || l1_status === 'CONTRACTOR' || l1_status === 'MANAGER')) || (role === 'contractor' && (l1_status === 'draft' || l1_status === 'CONTRACTOR')));
  const isLevel2Editable = !l2_manager_id && ((role === 'engineer' && (l2_status === 'draft' || l2_status === 'CONTRACTOR' || l2_status === 'MANAGER')) || (role === 'contractor' && (l2_status === 'draft' || l2_status === 'CONTRACTOR')));

  // Per-level general comments: editable by ALL until approved
  const canEditL1General = l1_status !== 'approved';
  const canEditL2General = l2_status !== 'approved';

  const disableLevel1Tab = role === 'contractor' && getValues('l1_status') !== 'draft' && getValues('l1_status') !== 'CONTRACTOR';
  const disableLevel2Tab = role === 'contractor' && getValues('l2_status') !== 'draft' && getValues('l2_status') !== 'CONTRACTOR';

  // ---------- Signature edit permissions ----------
  const canEditSignature = useCallback(
    (level, sigRole) => {
      const managerId = level === 'l1' ? getValues('l1_manager_id') : getValues('l2_manager_id');
      if (managerId) return false;
      const s = level === 'l1' ? getValues('l1_status') : getValues('l2_status');
      if (sigRole === 'charge_engineer' && role === 'engineer' && (s === 'draft' || s === 'CONTRACTOR' || s === 'MANAGER')) return true;
      if (sigRole === 'contractor' && role === 'contractor' && (s === 'draft' || s === 'CONTRACTOR')) return true;
      if (sigRole === 'quality' && role === 'quality' && s === 'QUALITY') return true;
      return false;
    },
    [role, getValues]
  );

  // ---------- Common status derivation ----------
  const deriveCommonStatus = (s1, s2) => {
    if (s1 === 'CONTRACTOR' && s2 === 'CONTRACTOR') return 'CONTRACTOR';
    if (s1 === 'MANAGER' && s2 === 'MANAGER') return 'MANAGER';
    if (s1 === 'QUALITY' && s2 === 'QUALITY') return 'QUALITY';
    if (s1 === 'approved' && s2 === 'approved') return 'approved';
    return 'draft';
  };

  // ---------- Autosave ----------
  const autoSave = useCallback(
    _.debounce((data) => {
      if (!dataLoadedRef.current) return;
      const overall = deriveCommonStatus(data.l1_status, data.l2_status);
      const payload = {
        form_data: {
          status: overall,
          form_id: formId,
          formName: 'ht_cabel',
          station_id: data.station_id,
          project_name: data.project_name,
          equipment: data.equipment,
          manufacturer: data.manufacturer,
          output: data.output,
          l1: {
            visual_checks: data.visual_checks,
            physical_checks: data.physical_checks,
            comments: data.l1_comments,
            other_comments: data.l1_other_comments,
            status: data.l1_status,
          },
          l2: {
            all_cables_healthy: data.all_cables_healthy,
            testing_commissioning: data.testing_commissioning,
            cables: data.cables,
            instruments: data.instruments,
            comments: data.l2_comments,
            other_comments: data.l2_other_comments,
            observations: data.l2_observations,
            status: data.l2_status,
          },
        },
      };
      dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
    }, 2000),
    [dispatch, formId]
  );

  useEffect(() => { autoSave(formValues); }, [formValues, autoSave]);

  // ---------- Load existing ----------
  useEffect(() => {
    if (formId) dispatch({ type: VIEW_FORM, payload: { formId, formName: 'ht_cabel' } });
    setIsLoading(false);
  }, [dispatch, formId]);

  useEffect(() => {
    if (viewFormData) {
      const data = viewFormData || {};
      setValue('station_id', data.station_id || stationId || '');
      setValue('project_name', data.project_name || '');
      setValue('equipment', data.equipment || '');
      setValue('manufacturer', data.manufacturer || '');
      setValue('output', data.output || '');

      setValue(
        'visual_checks',
        Array.isArray(data.l1?.visual_checks)
          ? data.l1.visual_checks
          : [
              { description: 'Have the correct methods of cable termination and tagging been checked?', status: '', remark: '' },
              { description: 'Has the cable TC been checked?', status: '', remark: '' },
            ]
      );
      setValue(
        'physical_checks',
        Array.isArray(data.l1?.physical_checks)
          ? data.l1.physical_checks
          : [
              { description: 'Are the busbar joints properly connected and tightened?', status: '', remark: '' },
              { description: 'Has the proper size lugs been used with the correct lugging tool?', status: '', remark: '' },
            ]
      );
      setValue(
        'l1_comments',
        data.l1?.comments || {
          contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
          charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
          quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
          optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        }
      );
      setValue('l1_other_comments', data.l1?.other_comments || '');
      setValue('l1_status', data.l1?.status || 'draft');
      setValue('l1_manager_id', data.l1?.managerId || null);

      setValue('all_cables_healthy', data.l2?.all_cables_healthy || '');
      setValue('testing_commissioning', data.l2?.testing_commissioning || '');
      setValue(
        'cables',
        Array.isArray(data.l2?.cables)
          ? data.l2.cables
          : Array(8)
              .fill({
                cable_number: 0,
                status: '',
                tag_no: '',
                cable_size: '',
                from: '',
                to: '',
                cable_length: '',
                r_y_continuity: '',
                y_b_continuity: '',
                b_r_continuity: '',
                r_n_continuity: '',
                y_n_continuity: '',
                b_n_continuity: '',
                r_e_continuity: '',
                y_e_continuity: '',
                b_e_continuity: '',
                n_e_continuity: '',
                r_y_ir_value: '',
                y_b_ir_value: '',
                b_r_ir_value: '',
                r_n_ir_value: '',
                y_n_ir_value: '',
                b_n_ir_value: '',
                r_e_ir_value: '',
                y_e_ir_value: '',
                b_e_ir_value: '',
                n_e_ir_value: '',
                healthy: '',
                extra_length_side: '',
              })
              .map((item, index) => ({ ...item, cable_number: index + 1 }))
      );
      setValue(
        'instruments',
        Array.isArray(data.l2?.instruments)
          ? data.l2.instruments
          : [
              { description: 'IR Tester', serial: '', range: '', calibration: '' },
              { description: 'Digital Multi meter / Tong Tester', serial: '', range: '', calibration: '' },
            ]
      );
      setValue(
        'l2_comments',
        data.l2?.comments || {
          contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
          charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
          quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
          optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        }
      );
      setValue('l2_other_comments', data.l2?.other_comments || '');
      setValue('l2_observations', data.l2?.observations || '');
      setValue('l2_status', data.l2?.status || 'draft');
      setValue('l2_manager_id', data.l2?.managerId || null);
      setValue('status', data.status || 'draft');
      dataLoadedRef.current = true;
    }
  }, [viewFormData, stationId, setValue]);

  // ---------- Signature handlers ----------
  const handleImageUpload = (e, level, sigRole) => {
    if (!canEditSignature(level, sigRole)) return toast.info('This signature is not editable for your role/stage');
    const file = e.target.files[0];
    const maxSizeInBytes = 0.05 * 1024 * 1024; // 50KB
    if (file) {
      if (file.size > maxSizeInBytes) {
        toast.error('File size exceeds 50KB. Please upload a smaller image.');
        handleResetSignature(level, sigRole);
        sigCanvas.current?.clear();
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => setValue(`${level}_comments.${sigRole}.signature`, reader.result, { shouldDirty: true, shouldValidate: true });
      reader.readAsDataURL(file);
    }
  };

  const handleSignatureTypeChange = (level, sigRole, type) => {
    if (!canEditSignature(level, sigRole)) return toast.info('This signature is not editable for your role/stage');
    setValue(`${level}_comments.${sigRole}.signatureType`, type, { shouldDirty: true, shouldValidate: true });
    setValue(`${level}_comments.${sigRole}.signature`, '', { shouldDirty: true, shouldValidate: true });
    if (type === 'digital') {
      setCurrentSignature({ level, role: sigRole });
      setShowModal(true);
    }
  };

  const handleResetSignature = (level, sigRole) => {
    if (!canEditSignature(level, sigRole)) return toast.info('This signature is not editable for your role/stage');
    setValue(`${level}_comments.${sigRole}.signature`, '', { shouldDirty: true, shouldValidate: true });
    setValue(`${level}_comments.${sigRole}.signatureType`, 'upload', { shouldDirty: true, shouldValidate: true });
  };

  const handleSaveSignature = () => {
    const { level, role: sigRole } = currentSignature;
    if (!canEditSignature(level, sigRole)) {
      setShowModal(false);
      return;
    }
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const signatureData = sigCanvas.current.toDataURL();
      setValue(`${level}_comments.${sigRole}.signature`, signatureData, { shouldDirty: true, shouldValidate: true });
    }
    setShowModal(false);
    sigCanvas.current?.clear();
  };

  const handleClearSignature = () => {
    sigCanvas.current?.clear();
  };

  const handleCloseModal = () => {
    setShowModal(false);
    sigCanvas.current?.clear();
  };

  // ---------- Submit transitions per-level ----------
  const nextStatusFor = (curr, action) => {
    if (role === 'engineer') return action === 'draft' ? 'draft' : 'CONTRACTOR';
    if (role === 'contractor') return action === 'draft' ? 'CONTRACTOR' : 'MANAGER';
    if (role === 'manager') return action === 'approved' ? 'QUALITY' : 'draft';
    if (role === 'quality') return action === 'approved' ? 'approved' : 'draft';
    return curr || 'draft';
  };

  const onSubmit = (level, action) => (data) => {
    const s1 = data.l1_status; const s2 = data.l2_status;
    const l1_next = level === 'l1' ? nextStatusFor(s1, action) : s1;
    const l2_next = level === 'l2' ? nextStatusFor(s2, action) : s2;
    const overall = deriveCommonStatus(l1_next, l2_next);

    const payload = {
      form_data: {
        status: overall,
        form_id: formId,
        formName: 'ht_cabel',
        station_id: data.station_id,
        project_name: data.project_name,
        equipment: data.equipment,
        manufacturer: data.manufacturer,
        output: data.output,
        l1: {
          visual_checks: data.visual_checks,
          physical_checks: data.physical_checks,
          comments: data.l1_comments,
          other_comments: data.l1_other_comments,
          status: l1_next,
        },
        l2: {
          all_cables_healthy: data.all_cables_healthy,
          testing_commissioning: data.testing_commissioning,
          cables: data.cables,
          instruments: data.instruments,
          comments: data.l2_comments,
          other_comments: data.l2_other_comments,
          observations: data.l2_observations,
          status: l2_next,
        },
      },
    };

    dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });

    const label = action === 'approved' ? 'Approved' : action === 'draft' ? 'Saved as Draft' : 'Submitted';
    toast.success(`Level ${level.toUpperCase()} ${label} Successfully`);
    navigate(-1);
  };

  // Button policies
  const submitDisabledL1 = (role === 'engineer' || role === 'contractor') ? (l1_status !== 'draft' && l1_status !== 'CONTRACTOR') : true;
  const submitDisabledL2 = (role === 'engineer' || role === 'contractor') ? (l2_status !== 'draft' && l2_status !== 'CONTRACTOR') : true;

  if (isLoading) return <div>Loading...</div>;

  return (
    <div className="page-body container-xl">
      <h2 className="page-title mb-4">HT Cable - Site Acceptance Testing</h2>

      <Form>
        <h3>1. Equipment Details</h3>
        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Station ID</Form.Label>
              <Form.Control type="text" {...register('station_id')} readOnly />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Project Name</Form.Label>
              <Form.Control type="text" {...register('project_name')} disabled={!isLevel1Editable} />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group className="mb-3">
              <Form.Label>Equipment Manufacturer</Form.Label>
              <Form.Control type="text" {...register('equipment')} disabled={!isLevel1Editable} />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group className="mb-3">
              <Form.Label>Output</Form.Label>
              <Form.Control type="text" {...register('output')} disabled={!isLevel1Editable} />
            </Form.Group>
          </Col>
        </Row>

        <Tabs defaultActiveKey="level1" id="sat-tabs" className="mb-3">
          {/* ---------------- Level 1 ---------------- */}
          <Tab eventKey="level1" title={`Level 1 - Pre-Commissioning (${l1_status})`} disabled={disableLevel1Tab}>
            <h3 className="mt-4">2. Visual Checks</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Remark</th>
                </tr>
              </thead>
              <tbody>
                {[
                  'Have the correct methods of cable termination and tagging been checked?',
                  'Has the cable TC been checked?',
                ].map((description, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{description}</td>
                    <td>
                      <Form.Check inline label="Yes" type="radio" name={`visual_checks_${index}`} value="Yes" {...register(`visual_checks.${index}.status`)} disabled={!isLevel1Editable} />
                      <Form.Check inline label="No" type="radio" name={`visual_checks_${index}`} value="No" {...register(`visual_checks.${index}.status`)} disabled={!isLevel1Editable} />
                      <Form.Check inline label="NA" type="radio" name={`visual_checks_${index}`} value="NA" {...register(`visual_checks.${index}.status`)} disabled={!isLevel1Editable} />
                    </td>
                    <td>
                      <Form.Control type="text" {...register(`visual_checks.${index}.remark`)} disabled={!isLevel1Editable} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">3. Physical Checks</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Remark</th>
                </tr>
              </thead>
              <tbody>
                {[
                  'Are the busbar joints properly connected and tightened?',
                  'Has the proper size lugs been used with the correct lugging tool?',
                ].map((description, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{description}</td>
                    <td>
                      <Form.Check inline label="Yes" type="radio" name={`physical_checks_${index}`} value="Yes" {...register(`physical_checks.${index}.status`)} disabled={!isLevel1Editable} />
                      <Form.Check inline label="No" type="radio" name={`physical_checks_${index}`} value="No" {...register(`physical_checks.${index}.status`)} disabled={!isLevel1Editable} />
                      <Form.Check inline label="NA" type="radio" name={`physical_checks_${index}`} value="NA" {...register(`physical_checks.${index}.status`)} disabled={!isLevel1Editable} />
                    </td>
                    <td>
                      <Form.Control type="text" {...register(`physical_checks.${index}.remark`)} disabled={!isLevel1Editable} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">4. Level 1 Pre-Commissioning Comments</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th></th>
                  <th>Engineer - Contractor</th>
                  <th>Engineer - Charge Zone</th>
                  <th>Quality - Charge Zone</th>
                  <th>Optional</th>
                </tr>
              </thead>
              <tbody>
                {['Name', 'Company', 'Signature', 'Date'].map((field) => (
                  <tr key={field}>
                    <td>{field}</td>
                    {['contractor', 'charge_engineer', 'quality', 'optional'].map((r) => {
                      const editable = canEditSignature('l1', r);
                      if (field === 'Date') {
                        return (
                          <td key={r}>
                            <Form.Control type="date" {...register(`l1_comments.${r}.date`)} disabled={!editable} />
                          </td>
                        );
                      } else if (field === 'Signature') {
                        return (
                          <td key={r}>
                            <Form.Select
                              {...register(`l1_comments.${r}.signatureType`)}
                              onChange={(e) => handleSignatureTypeChange('l1', r, e.target.value)}
                              className="mb-2"
                              disabled={!editable}
                            >
                              <option value="upload">Image Upload</option>
                              <option value="digital">Digital Sign</option>
                            </Form.Select>
                            {_.get(formValues, `l1_comments.${r}.signatureType`) === 'upload' && (
                              <Form.Control type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'l1', r)} className="mb-2" disabled={!editable} />
                            )}
                            {_.get(formValues, `l1_comments.${r}.signature`) && (
                              <div>
                                <img src={_.get(formValues, `l1_comments.${r}.signature`)} alt="Signature" style={{ maxWidth: '100px', marginTop: '5px' }} />
                                <Button variant="danger" size="sm" onClick={() => handleResetSignature('l1', r)} className="mt-2" disabled={!editable}>
                                  Reset Signature
                                </Button>
                              </div>
                            )}
                          </td>
                        );
                      }
                      return (
                        <td key={r}>
                          <Form.Control type="text" {...register(`l1_comments.${r}.${field.toLowerCase()}`)} disabled={!editable} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">5. Other Comments / Remarks</h3>
            <Form.Group className="mb-3">
              <Form.Control as="textarea" rows={4} {...register('l1_other_comments')} disabled={!canEditL1General} placeholder="Enter any additional comments or remarks for Level 1" />
            </Form.Group>

            {/* Level 1 actions */}
            {role === 'manager' && l1_status === 'MANAGER' ? (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l1', 'approved'))} className="me-2">
                  Approve (Send to QUALITY)
                </Button>
                <Button variant="danger" onClick={handleSubmit(onSubmit('l1', 'draft'))}>
                  Re-Work
                </Button>
              </div>
            ) : role === 'quality' && l1_status === 'QUALITY' ? (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l1', 'approved'))} className="me-2">
                  Final Approve
                </Button>
                <Button variant="danger" onClick={handleSubmit(onSubmit('l1', 'draft'))}>
                  Apply for Resubmit
                </Button>
              </div>
            ) : (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l1', 'pending'))} disabled={submitDisabledL1}>
                  Submit Level 1
                </Button>
                <Button variant="secondary" style={{ marginLeft: '10px' }} onClick={handleSubmit(onSubmit('l1', 'draft'))} disabled={submitDisabledL1}>
                  Save As Draft
                </Button>
              </div>
            )}
          </Tab>

          {/* ---------------- Level 2 ---------------- */}
          <Tab eventKey="level2" title={`Level 2 - Commissioning (${l2_status})`} disabled={disableLevel2Tab}>
            <h3 className="mt-4">
              Note: Ensure all below points are in co-ordinate with ANNEXURE: HT CABLE: COMMISSIONING PART: Level 2 and if all tests are found correct select Yes / No.
            </h3>

            <h3 className="mt-4">6. Commissioning Part</h3>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>All HT Cables Found Healthy &amp; Ready to Charge</Form.Label>
                  <Form.Select {...register('all_cables_healthy')} disabled={!isLevel2Editable}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Testing &amp; Commissioning</Form.Label>
                  <Form.Select {...register('testing_commissioning')} disabled={!isLevel2Editable}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <h3 className="mt-4">7. Individual Cable Checks</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 8 }, (_, i) => (
                  <tr key={i}>
                    <td>{`Cable -${i + 1} Is found Healthy and ready to charge`}</td>
                    <td>
                      <Form.Check inline label="Yes" type="radio" name={`cables_${i}_status`} value="Yes" {...register(`cables.${i}.status`)} disabled={!isLevel2Editable} />
                      <Form.Check inline label="No" type="radio" name={`cables_${i}_status`} value="No" {...register(`cables.${i}.status`)} disabled={!isLevel2Editable} />
                      <Form.Check inline label="NA" type="radio" name={`cables_${i}_status`} value="NA" {...register(`cables.${i}.status`)} disabled={!isLevel2Editable} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">8. Test Instruments Details and Calibration Status</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Instrument Description</th>
                  <th>Instrument Serial No.</th>
                  <th>Range</th>
                  <th>Calibration Status</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { description: 'IR Tester', serial: '', range: '', calibration: '' },
                  { description: 'Digital Multi meter / Tong Tester', serial: '', range: '', calibration: '' },
                ].map((instrument, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>
                      <Form.Control type="text" value={instrument.description} disabled />
                    </td>
                    <td>
                      <Form.Control type="text" {...register(`instruments.${index}.serial`)} disabled={!isLevel2Editable} />
                    </td>
                    <td>
                      <Form.Control type="text" {...register(`instruments.${index}.range`)} disabled={!isLevel2Editable} />
                    </td>
                    <td>
                      <Form.Control type="text" {...register(`instruments.${index}.calibration`)} disabled={!isLevel2Editable} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">TESTING &amp; COMMISSIONING Electrical Checks / Tests</h3>
            <h3 className="mt-4">9. Cable Details</h3>

            {Array.from({ length: 8 }, (_, cableIndex) => (
              <div key={cableIndex} className="mb-4">
                <h4>Cable {cableIndex + 1}</h4>
                <Row>
                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>Tag No</Form.Label>
                      <Form.Control type="text" {...register(`cables.${cableIndex}.tag_no`)} disabled={!isLevel2Editable} />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>Cable Size</Form.Label>
                      <Form.Control type="text" {...register(`cables.${cableIndex}.cable_size`)} disabled={!isLevel2Editable} />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>From</Form.Label>
                      <Form.Control type="text" {...register(`cables.${cableIndex}.from`)} disabled={!isLevel2Editable} />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>To</Form.Label>
                      <Form.Control type="text" {...register(`cables.${cableIndex}.to`)} disabled={!isLevel2Editable} />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>Cable Length (M)</Form.Label>
                      <Form.Control type="text" {...register(`cables.${cableIndex}.cable_length`)} disabled={!isLevel2Editable} />
                    </Form.Group>
                  </Col>
                </Row>
                <Table bordered responsive>
                  <thead>
                    <tr>
                      <th>Parameter</th>
                      <td>R-Y</td>
                      <td>Y-B</td>
                      <td>B-R</td>
                      <td>R-N</td>
                      <td>Y-N</td>
                      <td>B-N</td>
                      <td>R-E</td>
                      <td>Y-E</td>
                      <td>B-E</td>
                      <td>N-E</td>
                    </tr>
                    <tr>
                      <th>Continuity</th>
                      <td><Form.Control type="text" {...register(`cables.${cableIndex}.r_y_continuity`)} disabled={!isLevel2Editable} /></td>
                      <td><Form.Control type="text" {...register(`cables.${cableIndex}.y_b_continuity`)} disabled={!isLevel2Editable} /></td>
                      <td><Form.Control type="text" {...register(`cables.${cableIndex}.b_r_continuity`)} disabled={!isLevel2Editable} /></td>
                      <td><Form.Control type="text" {...register(`cables.${cableIndex}.r_n_continuity`)} disabled={!isLevel2Editable} /></td>
                      <td><Form.Control type="text" {...register(`cables.${cableIndex}.y_n_continuity`)} disabled={!isLevel2Editable} /></td>
                      <td><Form.Control type="text" {...register(`cables.${cableIndex}.b_n_continuity`)} disabled={!isLevel2Editable} /></td>
                      <td><Form.Control type="text" {...register(`cables.${cableIndex}.r_e_continuity`)} disabled={!isLevel2Editable} /></td>
                      <td><Form.Control type="text" {...register(`cables.${cableIndex}.y_e_continuity`)} disabled={!isLevel2Editable} /></td>
                      <td><Form.Control type="text" {...register(`cables.${cableIndex}.b_e_continuity`)} disabled={!isLevel2Editable} /></td>
                      <td><Form.Control type="text" {...register(`cables.${cableIndex}.n_e_continuity`)} disabled={!isLevel2Editable} /></td>
                    </tr>
                    <tr>
                      <th>IR Values</th>
                      <td><Form.Control type="text" {...register(`cables.${cableIndex}.r_y_ir_value`)} disabled={!isLevel2Editable} /></td>
                      <td><Form.Control type="text" {...register(`cables.${cableIndex}.y_b_ir_value`)} disabled={!isLevel2Editable} /></td>
                      <td><Form.Control type="text" {...register(`cables.${cableIndex}.b_r_ir_value`)} disabled={!isLevel2Editable} /></td>
                      <td><Form.Control type="text" {...register(`cables.${cableIndex}.r_n_ir_value`)} disabled={!isLevel2Editable} /></td>
                      <td><Form.Control type="text" {...register(`cables.${cableIndex}.y_n_ir_value`)} disabled={!isLevel2Editable} /></td>
                      <td><Form.Control type="text" {...register(`cables.${cableIndex}.b_n_ir_value`)} disabled={!isLevel2Editable} /></td>
                      <td><Form.Control type="text" {...register(`cables.${cableIndex}.r_e_ir_value`)} disabled={!isLevel2Editable} /></td>
                      <td><Form.Control type="text" {...register(`cables.${cableIndex}.y_e_ir_value`)} disabled={!isLevel2Editable} /></td>
                      <td><Form.Control type="text" {...register(`cables.${cableIndex}.b_e_ir_value`)} disabled={!isLevel2Editable} /></td>
                      <td><Form.Control type="text" {...register(`cables.${cableIndex}.n_e_ir_value`)} disabled={!isLevel2Editable} /></td>
                    </tr>
                  </thead>
                  <tbody></tbody>
                </Table>
              </div>
            ))}

            <h3 className="mt-4">10. Observations During Commissioning</h3>
            <Form.Group className="mb-3">
              <Form.Control as="textarea" rows={4} {...register('l2_observations')} disabled={!isLevel2Editable} placeholder="Enter observations during commissioning" />
            </Form.Group>

            <h3 className="mt-4">11. Level 2 Commissioning Comments</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th></th>
                  <th>Engineer - Contractor</th>
                  <th>Engineer - Charge Zone</th>
                  <th>Quality - Charge Zone</th>
                  <th>Optional</th>
                </tr>
              </thead>
              <tbody>
                {['Name', 'Company', 'Signature', 'Date'].map((field) => (
                  <tr key={field}>
                    <td>{field}</td>
                    {['contractor', 'charge_engineer', 'quality', 'optional'].map((r) => {
                      const editable = canEditSignature('l2', r);
                      if (field === 'Date') {
                        return (
                          <td key={r}>
                            <Form.Control type="date" {...register(`l2_comments.${r}.date`)} disabled={!editable} />
                          </td>
                        );
                      } else if (field === 'Signature') {
                        return (
                          <td key={r}>
                            <Form.Select
                              {...register(`l2_comments.${r}.signatureType`)}
                              onChange={(e) => handleSignatureTypeChange('l2', r, e.target.value)}
                              className="mb-2"
                              disabled={!editable}
                            >
                              <option value="upload">Image Upload</option>
                              <option value="digital">Digital Sign</option>
                            </Form.Select>
                            {_.get(formValues, `l2_comments.${r}.signatureType`) === 'upload' && (
                              <Form.Control type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'l2', r)} className="mb-2" disabled={!editable} />
                            )}
                            {_.get(formValues, `l2_comments.${r}.signature`) && (
                              <div>
                                <img src={_.get(formValues, `l2_comments.${r}.signature`)} alt="Signature" style={{ maxWidth: '100px', marginTop: '5px' }} />
                                <Button variant="danger" size="sm" onClick={() => handleResetSignature('l2', r)} className="mt-2" disabled={!editable}>
                                  Reset Signature
                                </Button>
                              </div>
                            )}
                          </td>
                        );
                      }
                      return (
                        <td key={r}>
                          <Form.Control type="text" {...register(`l2_comments.${r}.${field.toLowerCase()}`)} disabled={!editable} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">12. Other Comments / Remarks</h3>
            <Form.Group className="mb-3">
              <Form.Control as="textarea" rows={4} {...register('l2_other_comments')} disabled={!canEditL2General} placeholder="Enter any additional comments or remarks for Level 2" />
            </Form.Group>

            {/* Level 2 actions */}
            {role === 'manager' && l2_status === 'MANAGER' ? (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l2', 'approved'))} className="me-2">
                  Approve (Send to QUALITY)
                </Button>
                <Button variant="danger" onClick={handleSubmit(onSubmit('l2', 'draft'))}>
                  Re-Work
                </Button>
              </div>
            ) : role === 'quality' && l2_status === 'QUALITY' ? (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l2', 'approved'))} className="me-2">
                  Final Approve
                </Button>
                <Button variant="danger" onClick={handleSubmit(onSubmit('l2', 'draft'))}>
                  Apply for Resubmit
                </Button>
              </div>
            ) : (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l2', 'pending'))} disabled={submitDisabledL2}>
                  Submit Level 2
                </Button>
                <Button variant="secondary" style={{ marginLeft: '10px' }} onClick={handleSubmit(onSubmit('l2', 'draft'))} disabled={submitDisabledL2}>
                  Save As Draft
                </Button>
              </div>
            )}
          </Tab>
        </Tabs>

        {/* Signature modal */}
        <Modal show={showModal} onHide={handleCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>Draw Signature</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <SignatureCanvas
              ref={sigCanvas}
              penColor="black"
              canvasProps={{ width: 500, height: 200, className: 'sigCanvas border' }}
            />
            <div className="mt-2">
              <Button variant="secondary" onClick={handleClearSignature} className="me-2">
                Clear
              </Button>
              <Button variant="primary" onClick={handleSaveSignature}>
                Save Signature
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </Form>
    </div>
  );
};

HTCableForm.propTypes = {
  role: PropTypes.oneOf(['engineer', 'contractor', 'manager', 'quality', 'admin']),
};

export default HTCableForm;
